import React, { useState, useEffect, useMemo } from 'react';
import { Product, OrderItem } from '../types';
import { CheckCircle, ShoppingCart, Lock, Plus, AlertTriangle, LogOut, User, Eye, ArrowDown, ArrowRight } from 'lucide-react';
import TrustStrip from './TrustStrip';
import { displayPrice } from '../constants';
import { RetailerUser } from '../services/authService';
import ProductDetailModal from './ProductDetailModal';

interface MixAndMatchBuilderProps {
  products: Product[];
  onCartUpdate: (count: number, totalValue: number, items: OrderItem[]) => void;
  openCheckout: () => void;
  isVerified?: boolean;
  onLoginClick?: () => void;
  retailerUser?: RetailerUser | null;
  onLogout?: () => void;
  onViewProfile?: (slug: string) => void;
}

// Product family groupings - products that are the same style but different sizes
// Updated 2026-05-25 (Master Jay): N (Bamba) removed, K is now Buriquito,
// L is its own specialty SKU (Incolma 202T) — no longer grouped with Rula.
const PRODUCT_FAMILIES: { [familyId: string]: string[] } = {
  '3canales': ['A1', 'A2'],
  'rozador': ['B1', 'B2'],
  'rula': ['C1', 'C2'],
  'barrigon': ['D'],
  'pacora': ['E'],
  'bananera': ['F1', 'F2'],
  'guaduero': ['G'],
  'cuma': ['H1', 'H2'],
  'recto': ['I1', 'I3'],
  'colima': ['J1', 'J2'],
  'buriquita': ['K'],
  'specialty_l': ['L'],
  'tranzidor': ['M'],
  'millfile': ['O'],
  'okapi': ['P'],
};

// Incolma reference number per family — the actual PN we carry, displayed as "Incolma {ref}".
// Buriquito and Mill File have no Incolma ref number and fall back to the category name.
const MODEL_NUMBERS: { [familyId: string]: string } = {
  '3canales':    '702W',
  'rozador':     '448',
  'rula':        '32',
  'barrigon':    '1820C',
  'pacora':      '1820',
  'bananera':    '26620',
  'guaduero':    '1004',
  'cuma':        '1807',
  'recto':       '152',
  'colima':      '127',
  'specialty_l': '202T',
};

const getFamilyId = (productId: string): string => {
  for (const [familyId, productIds] of Object.entries(PRODUCT_FAMILIES)) {
    if (productIds.includes(productId)) {
      return familyId;
    }
  }
  return productId;
};

interface ProductFamily {
  familyId: string;
  displayProduct: Product;
  variants: Product[];
  category: string;
}

const MixAndMatchBuilder: React.FC<MixAndMatchBuilderProps> = ({
  products,
  onCartUpdate,
  openCheckout,
  isVerified = false,
  onLoginClick,
  retailerUser,
  onLogout,
  onViewProfile,
}) => {
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [totalSelected, setTotalSelected] = useState(0);
  const [cartValue, setCartValue] = useState(0);
  const [selectedFamily, setSelectedFamily] = useState<ProductFamily | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Retail buyers have no minimum; wholesale accounts keep the 12-unit MOQ.
  const MOQ = isVerified ? 12 : 1;

  const productFamilies = useMemo(() => {
    const families: ProductFamily[] = [];
    const processedFamilies = new Set<string>();

    const sortedProducts = [...products].sort((a, b) => {
      const aIndex = Object.values(PRODUCT_FAMILIES).flat().indexOf(a.id);
      const bIndex = Object.values(PRODUCT_FAMILIES).flat().indexOf(b.id);
      return aIndex - bIndex;
    });

    for (const product of sortedProducts) {
      const familyId = getFamilyId(product.id);

      if (!processedFamilies.has(familyId)) {
        processedFamilies.add(familyId);

        const familyProductIds = PRODUCT_FAMILIES[familyId] || [product.id];
        const variants = familyProductIds
          .map((id) => products.find((p) => p.id === id))
          .filter((p): p is Product => p !== undefined)
          .sort((a, b) => b.bladeLength - a.bladeLength); // DESC: biggest blade first

        if (variants.length > 0) {
          families.push({
            familyId,
            displayProduct: variants[0], // displayProduct = biggest blade variant
            variants,
            category: variants[0].category,
          });
        }
      }
    }

    return families;
  }, [products]);

  useEffect(() => {
    let count = 0;
    let value = 0;
    const currentItems: OrderItem[] = [];

    Object.entries(quantities).forEach(([id, qty]) => {
      if (qty > 0) {
        count += qty;
        const product = products.find((p) => p.id === id);
        if (product) {
          const unitPrice = displayPrice(product, isVerified);
          value += qty * unitPrice;
          currentItems.push({
            productId: product.id,
            sku: product.sku,
            name: product.name,
            quantity: qty,
            price: unitPrice,
          });
        }
      }
    });

    setTotalSelected(count);
    setCartValue(value);
    onCartUpdate(count, value, currentItems);
  }, [quantities, products, onCartUpdate, isVerified]);

  const handleIncrement = (product: Product) => {
    if (quantities[product.id] && quantities[product.id] >= product.stock) return;
    setQuantities((prev) => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
  };

  const handleDecrement = (productId: string) => {
    if (!quantities[productId] || quantities[productId] <= 0) return;
    setQuantities((prev) => ({ ...prev, [productId]: prev[productId] - 1 }));
  };

  const openProductModal = (family: ProductFamily) => {
    setSelectedFamily(family);
    setIsModalOpen(true);
  };

  const closeProductModal = () => {
    setIsModalOpen(false);
    setSelectedFamily(null);
  };

  const getFamilyQuantity = (family: ProductFamily): number => {
    return family.variants.reduce((sum, variant) => sum + (quantities[variant.id] || 0), 0);
  };

  const remaining = Math.max(0, MOQ - totalSelected);
  const isMoqMet = totalSelected >= MOQ;
  const progressPercent = Math.min((totalSelected / MOQ) * 100, 100);

  let familyIndex = 0;

  return (
    <div className="bg-paper min-h-screen pb-24">

      {/* Header */}
      <header
        className="relative bg-forge text-paper py-24 lg:py-28 px-6 lg:px-10 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/machete-wall.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(14,21,24,0.95) 0%, rgba(14,21,24,0.78) 55%, rgba(14,21,24,0.55) 100%)',
          }}
        />
        {/* Rotating-machete video removed 2026-09-28 (owner: footage incorrect,
            re-shooting). When the new clip is ready, restore the split header
            layout from git history and serve the new file under a NEW filename. */}
        <div className="relative max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-6">
            {isVerified ? '— Wholesale ordering' : '— Shop the collection'}
          </p>
          <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-6 max-w-prose">
            {isVerified ? 'Build your order.' : 'Buy direct.'}
          </h1>
          <p className="text-paper/70 text-lg max-w-prose leading-[1.6]">
            {isVerified
              ? 'Select your inventory. Mix and match profiles to suit your local customer base.'
              : 'Drop-forged Colombian machetes, shipped to your door. Pick your blades and check out — no minimum order.'}
          </p>
          <p className="mt-6 font-mono text-xs tracking-[0.22em] uppercase text-brass">
            Blade sharp. Yard clean.
          </p>
        </div>
        {/* Bottom brass rule */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      {/* Trust strip — the assurances a first-time buyer (and underwriter) looks for */}
      <TrustStrip />

      {/* Retailer Welcome Bar */}
      {retailerUser && (
        <div className="bg-paper-bright border-b border-ink-200 py-3 px-6 lg:px-10">
          <div className="max-w-editorial mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-olive/15 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-olive" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-ink-900">
                  Welcome,{' '}
                  <span className="font-editorial italic text-ink-950">{retailerUser.businessName}</span>
                </p>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mt-0.5">
                  Wholesale pricing unlocked
                </p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ember transition-colors duration-150"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Featured profiles */}
      <section className="bg-paper-bright border-b border-ink-200 py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16">
            <div>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-4">
                — Featured profiles
              </p>
              <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 leading-[1.05] tracking-tight">
                The best-sellers.
              </h2>
            </div>
            <a
              href="#catalog-grid"
              className="group inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ember transition-colors duration-150"
            >
              Browse the full catalog
              <ArrowDown className="w-3.5 h-3.5 transition-transform duration-200 ease-editorial group-hover:translate-y-0.5" strokeWidth={1.5} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                image: '/images/products/tranzidor-45.webp',
                label: 'Saw + machete · 18"',
                name: 'Tranzidor 45',
                slug: 'tranzidor-45',
                body: 'The dual-function field tool — a sharpened machete edge on one side, serrated saw teeth along the spine. Clear brush with the blade, cut branches and small timber with the saw. One tool, two jobs.',
              },
              {
                image: '/images/products/M1.webp',
                label: 'Tres Canales · 22", 20"',
                name: 'Incolma 702W',
                slug: 'incolma-702w',
                body: 'The iconic three-fuller Latin machete — a straight working profile with three longitudinal grooves that lighten the blade without sacrificing strength. The everyday workhorse for brush, trail, and farm.',
              },
              {
                image: '/images/products/M4.webp',
                label: 'Compact agricultural · 14.5"',
                name: 'Incolma 1820',
                slug: 'incolma-1820',
                body: 'A compact agricultural workhorse with a moderate belly curve. Sized for orchard, smallhold, and homestead work — solid chopping through fruit-tree branches without a full-size blade.',
              },
            ].map((item) => (
              <article key={item.name} className="group">
                <div className="aspect-[4/3] bg-paper overflow-hidden border border-ink-200 group-hover:border-ink-300 mb-6 transition-colors duration-300 ease-editorial flex items-center justify-center p-2">
                  <img
                    src={item.image}
                    alt={`${item.name} — editorial product hero`}
                    className="max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-[1.02] transition-transform duration-700 ease-editorial"
                  />
                </div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-2">
                  {item.label}
                </p>
                <h3 className="font-editorial italic text-2xl text-ink-950 tracking-tight mb-3">
                  {item.name}.
                </h3>
                <p className="text-sm text-ink-700 leading-relaxed max-w-prose">
                  {item.body}
                </p>
                {onViewProfile && (
                  <button
                    onClick={() => onViewProfile(item.slug)}
                    className="mt-4 inline-flex items-center gap-2 text-sm text-ink-900 hover:text-ember transition-colors duration-150 border-b border-ink-300 hover:border-ember pb-0.5"
                  >
                    Read the full profile
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky Checkout Bar */}
      <div className="sticky top-20 z-40 bg-paper-bright/95 backdrop-blur-sm border-b border-ink-200 shadow-rest py-4 px-6 lg:px-10 transition-all duration-300 ease-editorial">
        <div className="max-w-editorial mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">

          {isVerified ? (
            <>
              {/* Wholesale: MOQ progress */}
              <div className="flex items-center gap-4 w-full">
                <div className="relative flex-shrink-0">
                  <ShoppingCart
                    className={`w-8 h-8 transition-colors duration-300 ${
                      isMoqMet ? 'text-ember' : 'text-ink-300'
                    }`}
                    strokeWidth={1.5}
                  />
                  {isMoqMet && (
                    <CheckCircle className="w-4 h-4 text-ember absolute -top-1 -right-1 bg-paper-bright rounded-full" />
                  )}
                </div>
                <div className="flex-1 max-w-2xl">
                  <div className="flex justify-between font-mono text-[10px] tracking-micro uppercase mb-2">
                    <span className="text-ink-900">
                      {isMoqMet ? `Minimum met · ${totalSelected} items` : 'Order minimum progress'}
                    </span>
                    <span className={isMoqMet ? 'text-ember' : 'text-ink-500'}>
                      {isMoqMet ? 'Ready to submit' : `${remaining} more to checkout`}
                    </span>
                  </div>
                  <div className="w-full bg-ink-100 h-[3px] overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ease-editorial ${
                        isMoqMet ? 'bg-ember' : 'bg-ink-700'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="w-full sm:w-auto flex flex-col items-end gap-2">
                <button
                  onClick={openCheckout}
                  disabled={!isMoqMet}
                  className={`w-full sm:w-auto px-7 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2 ${
                    isMoqMet
                      ? 'bg-ember text-paper-warm hover:bg-ember-deep'
                      : 'bg-ink-100 text-ink-500 cursor-not-allowed border border-ink-200'
                  }`}
                >
                  {isMoqMet ? (
                    <>
                      <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                      Submit request ({totalSelected} items)
                    </>
                  ) : (
                    <>
                      <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Review order request
                    </>
                  )}
                </button>
                {!isMoqMet && (
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                    <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                    Min order: {MOQ}. Add {remaining} more.
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Retail: simple cart summary */}
              <div className="flex items-center gap-4 w-full">
                <div className="relative flex-shrink-0">
                  <ShoppingCart
                    className={`w-8 h-8 transition-colors duration-300 ${
                      totalSelected > 0 ? 'text-ember' : 'text-ink-300'
                    }`}
                    strokeWidth={1.5}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-ink-900 text-sm">
                    {totalSelected > 0 ? (
                      <>
                        <span className="font-medium">{totalSelected}</span>{' '}
                        {totalSelected === 1 ? 'item' : 'items'} in your cart
                      </>
                    ) : (
                      'Your cart is empty'
                    )}
                  </p>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mt-1">
                    {totalSelected > 0 ? `Subtotal $${cartValue.toFixed(2)}` : 'Add blades to get started'}
                  </p>
                </div>
              </div>

              {/* Action */}
              <div className="w-full sm:w-auto">
                <button
                  onClick={openCheckout}
                  disabled={totalSelected === 0}
                  className={`w-full sm:w-auto px-7 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2 ${
                    totalSelected > 0
                      ? 'bg-ember text-paper-warm hover:bg-ember-deep'
                      : 'bg-ink-100 text-ink-500 cursor-not-allowed border border-ink-200'
                  }`}
                >
                  <ShoppingCart className="w-4 h-4" strokeWidth={1.5} />
                  {totalSelected > 0 ? `Checkout · $${cartValue.toFixed(2)}` : 'Checkout'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Product Grid */}
      <div id="catalog-grid" className="max-w-editorial mx-auto px-6 lg:px-10 py-16 lg:py-20 scroll-mt-24">

        {/* Grid header */}
        <div className="mb-12 border-b border-ink-200 pb-8">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-4">
            — Catalog · {productFamilies.length} profiles
          </p>
          <h2 className="font-editorial italic font-light text-3xl lg:text-4xl text-ink-950 leading-tight tracking-tight">
            The collection.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-12">
          {productFamilies.map((family) => {
            familyIndex++;
            const familyQty = getFamilyQuantity(family);
            const displayProduct = family.displayProduct;
            const hasMultipleSizes = family.variants.length > 1;
            const minPrice = Math.min(...family.variants.map((v) => displayPrice(v, isVerified)));

            return (
              <article
                key={family.familyId}
                onClick={() => openProductModal(family)}
                className="group cursor-pointer flex flex-col"
              >
                {/* Image */}
                <div className="relative aspect-square bg-paper-bright border border-ink-200 group-hover:border-ink-300 transition-colors duration-300 ease-editorial overflow-hidden p-8 flex items-center justify-center">

                  {/* Catalog number */}
                  <span className="absolute top-3 left-3 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                    M{familyIndex}
                  </span>

                  {/* In stock */}
                  <span className="absolute top-3 right-3 font-mono text-[10px] tracking-micro uppercase text-olive">
                    In stock
                  </span>

                  {/* Size variants */}
                  {hasMultipleSizes && (
                    <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-wider text-ink-700 bg-paper-bright/90 backdrop-blur-sm px-2 py-1">
                      {family.variants.map((v) => `${v.bladeLength}"`).join(' · ')}
                    </span>
                  )}

                  <img
                    src={displayProduct.image}
                    alt={displayProduct.name}
                    className="w-full h-full object-contain mix-blend-multiply transition-transform duration-500 ease-editorial group-hover:scale-[1.04]"
                  />

                  {/* Quantity overlay */}
                  {familyQty > 0 && (
                    <span className="absolute top-3 right-3 bg-ember text-paper-warm w-8 h-8 flex items-center justify-center font-mono font-medium text-sm animate-in zoom-in rounded-full">
                      {familyQty}
                    </span>
                  )}

                  {/* Hover view-details */}
                  <div className="absolute inset-0 bg-forge-deep/0 group-hover:bg-forge-deep/10 transition-colors duration-300 ease-editorial flex items-center justify-center pointer-events-none">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-editorial inline-flex items-center gap-2 bg-paper-bright/95 backdrop-blur-sm px-4 py-2 border border-ink-200">
                      <Eye className="w-3.5 h-3.5 text-ember" strokeWidth={1.5} />
                      <span className="font-mono text-[11px] tracking-micro uppercase text-ink-900">View</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                      {family.category}
                    </span>
                    {hasMultipleSizes && (
                      <span className="font-mono text-[10px] tracking-micro uppercase text-ember-deep">
                        {family.variants.length} sizes
                      </span>
                    )}
                  </div>

                  <h3 className="font-editorial italic text-xl text-ink-950 tracking-tight leading-tight mb-2">
                    {MODEL_NUMBERS[family.familyId] ? `Incolma ${MODEL_NUMBERS[family.familyId]}` : family.category}.
                  </h3>

                  <p className="text-sm text-ink-700 leading-relaxed mb-5 line-clamp-2 flex-1">
                    {displayProduct.description}
                  </p>

                  {/* Price */}
                  <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-ink-200">
                    <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">From · CAD</span>
                    <span className="font-editorial italic text-2xl text-ink-950">
                      ${minPrice.toFixed(2)}
                    </span>
                  </div>

                  {/* CTA */}
                  <div className="mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openProductModal(family);
                      }}
                      className="w-full py-3 bg-forge text-paper text-sm font-medium hover:bg-ember transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                      View & add
                    </button>
                  </div>
                </div>
              </article>
            );
          })}

          {/* Expand range placeholder */}
          <div className="flex flex-col items-center justify-center p-12 text-center group cursor-pointer border border-dashed border-ink-200 hover:border-ink-300 transition-colors duration-300 ease-editorial">
            <div className="w-14 h-14 rounded-full bg-paper-bright border border-ink-200 flex items-center justify-center mb-5 group-hover:border-ink-300 transition-colors duration-300 ease-editorial">
              <Plus className="w-6 h-6 text-ink-300 group-hover:text-ember transition-colors duration-300 ease-editorial" strokeWidth={1.5} />
            </div>
            <h3 className="font-editorial italic text-xl text-ink-500 group-hover:text-ember transition-colors duration-300 ease-editorial">
              Expand range.
            </h3>
            <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mt-3">
              New profiles · Q4
            </p>
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedFamily && (
        <ProductDetailModal
          products={selectedFamily.variants}
          isOpen={isModalOpen}
          onClose={closeProductModal}
          isVerified={isVerified}
          onLoginClick={onLoginClick}
          quantities={quantities}
          onIncrement={handleIncrement}
          onDecrement={handleDecrement}
          modelNumber={MODEL_NUMBERS[selectedFamily.familyId]}
        />
      )}
    </div>
  );
};

export default MixAndMatchBuilder;
